<template>
  <div>
    <div class="configurator-actions">
      <div>
        <div class="configurator-title">
          {{ $t('configurator.selection_title') }}
        </div>
        <div class="configurator-subtitle">
          {{ $t('configurator.selection_subtitle') }}
        </div>
      </div>
      <div
        v-if="items.length"
        class="d-flex align-items-center"
      >
        <span class="cart-badge mr-50">
          {{ $tc('configurator.curtains_in_offer', items.length, { count: items.length }) }}
        </span>
        <b-button
          variant="primary"
          class="cfg-btn"
          @click="$emit('next')"
        >
          {{ $t('configurator.continue') }} →
        </b-button>
      </div>
    </div>

    <div class="search-wrap mb-1">
      <feather-icon
        icon="SearchIcon"
        class="search-icon"
      />
      <b-form-input
        v-model="search"
        :placeholder="$t('configurator.search')"
      />
    </div>

    <div class="families">
      <div
        v-for="family in families"
        :key="family.key"
      >
        <div
          class="banner-title"
          :style="{ backgroundImage: `url(${family.banner})` }"
        >
          <span>{{ family.label }}</span>
        </div>
        <div class="product-grid">
          <div
            v-for="curtain in family.curtains"
            :key="curtainKey(curtain)"
            class="product-card"
          >
            <div>
              <div class="d-flex align-items-start justify-content-between">
                <div class="product-name">
                  {{ curtain.name }}
                </div>
                <span
                  class="status-badge ml-50"
                  :class="curtain.sin_tarifa ? 'status-badge-orange' : 'status-badge-green'"
                >
                  {{ curtain.sin_tarifa ? $t('configurator.price_pending') : $t('configurator.available') }}
                </span>
              </div>
              <div class="product-ref">
                {{ curtain.ref }}
              </div>
              <div
                v-if="curtain.sin_tarifa"
                class="validation-notice"
              >
                <feather-icon
                  icon="InfoIcon"
                  size="12"
                />
                {{ $t('configurator.price_pending_short') }}
              </div>
            </div>
            <div>
              <div class="add-row">
                <div class="qty-stepper">
                  <button
                    type="button"
                    class="qty-stepper-btn"
                    @click="changeQty(curtain, -1)"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    class="qty-input"
                    min="1"
                    :max="maxUnits"
                    :value="qty(curtain)"
                    @change="setQty(curtain, $event.target.value)"
                  >
                  <button
                    type="button"
                    class="qty-stepper-btn"
                    @click="changeQty(curtain, 1)"
                  >
                    +
                  </button>
                </div>
                <b-button
                  :variant="curtain.sin_tarifa ? 'outline-warning' : 'primary'"
                  size="sm"
                  class="add-btn"
                  :class="{ 'add-btn-pending': curtain.sin_tarifa }"
                  @click="add(curtain)"
                >
                  + {{ $t('configurator.add') }}
                </b-button>
              </div>
              <a
                href="#"
                class="saber-mas"
                @click.prevent="info = curtain"
              >{{ $t('configurator.more_info') }}</a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <b-modal
      :visible="!!info"
      :title="info ? info.name : ''"
      hide-footer
      centered
      @hidden="info = null"
    >
      <template v-if="info">
        <div
          v-if="info.sin_tarifa"
          class="validation-notice mb-1"
        >
          <feather-icon
            icon="InfoIcon"
            size="12"
          />
          {{ $t('configurator.price_pending_help') }}
        </div>
        <div
          v-if="info.description"
          class="small text-muted mb-1"
          v-html="info.description"
        />
        <div class="spec-row">
          <span>{{ $t('configurator.reference') }}</span><strong>{{ info.ref }}</strong>
        </div>
        <div class="spec-row">
          <span>{{ $t('configurator.max_length_label') }}</span><strong>12.000 mm</strong>
        </div>
        <div class="spec-row">
          <span>{{ $t('configurator.max_height_label') }}</span><strong>{{ info.serie ? '10.000' : '8.000' }} mm</strong>
        </div>
        <div
          v-if="info.control_box"
          class="spec-row"
        >
          <span>{{ $t('configurator.motor') }}</span><strong>{{ $t('configurator.left') }} / {{ $t('configurator.right') }}</strong>
        </div>
        <div class="d-flex mt-1">
          <b-button
            variant="primary"
            class="modal-btn mr-50"
            @click="add(info); info = null"
          >
            + {{ $t('configurator.add_to_offer') }}
          </b-button>
          <b-button
            variant="outline-secondary"
            class="modal-btn"
            @click="info = null"
          >
            {{ $t('configurator.close') }}
          </b-button>
          <b-link
            v-if="info.url_web"
            :href="info.url_web"
            target="_blank"
            class="ml-auto align-self-center small"
          >
            {{ $t('configurator.product_sheet') }}
          </b-link>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import {
  BButton, BFormInput, BLink, BModal,
} from 'bootstrap-vue'
import fireBanner from '@/assets/images/rater/cortafuegos.png'
import smokeBanner from '@/assets/images/rater/humos.png'

const MAX_UNITS = 20

export default {
  components: {
    BButton, BFormInput, BLink, BModal,
  },
  data() {
    return {
      search: '',
      quantities: {},
      info: null,
      maxUnits: MAX_UNITS,
    }
  },
  computed: {
    ...mapGetters({
      catalog: 'configurator/getCatalog',
      items: 'configurator/getItems',
    }),
    families() {
      const search = this.search.toLowerCase()
      const curtains = this.catalog.curtains.filter(c => !search || `${c.name} ${c.ref}`.toLowerCase().includes(search))
      return [
        {
          key: 'fire', label: this.$t('configurator.fire_curtains'), banner: fireBanner, curtains: curtains.filter(c => c.family === 'fire'),
        },
        {
          key: 'smoke', label: this.$t('configurator.smoke_curtains'), banner: smokeBanner, curtains: curtains.filter(c => c.family === 'smoke'),
        },
      ]
    },
  },
  methods: {
    ...mapMutations({
      addCurtains: 'configurator/addCurtains',
      notifications: 'notifications',
      setShowNotifications: 'setShowNotifications',
    }),
    curtainKey(curtain) {
      return `${curtain.product_id}-${curtain.serie || ''}`
    },
    qty(curtain) {
      return this.quantities[this.curtainKey(curtain)] || 1
    },
    setQty(curtain, value) {
      this.$set(this.quantities, this.curtainKey(curtain), Math.max(1, Math.min(MAX_UNITS, parseInt(value, 10) || 1)))
    },
    changeQty(curtain, delta) {
      this.setQty(curtain, this.qty(curtain) + delta)
    },
    add(curtain) {
      const quantity = this.qty(curtain)
      this.addCurtains({ curtain, quantity })
      this.$set(this.quantities, this.curtainKey(curtain), 1)
      this.notifications({ title: this.$tc('configurator.units_added', quantity, { count: quantity }), variant: 'success' })
      this.setShowNotifications(true)
    },
  },
}
</script>

<style lang="scss" scoped>
.search-wrap {
  position: relative;
  max-width: 360px;
  .form-control {
    padding-left: 2.25rem;
  }
  .search-icon {
    position: absolute;
    left: .75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #82868b;
    pointer-events: none;
  }
}
.families {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  @media (max-width: 991px) {
    grid-template-columns: 1fr;
  }
}
.banner-title {
  padding: 2.5rem 1rem;
  margin-bottom: .75rem;
  text-align: center;
  border-radius: 8px;
  background-color: #4d4d4d;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center;
  span {
    color: #fff;
    font-weight: 600;
    font-size: 1.5rem;
    text-shadow: 0 1px 4px rgba(0, 0, 0, .45);
  }
}
.product-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: .625rem;
  margin-bottom: 1rem;
  @media (max-width: 575px) {
    grid-template-columns: 1fr;
  }
}
.product-card {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: .875rem 1rem;
  background: #fff;
  border: 2px solid transparent;
  border-radius: .428rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, .1);
  transition: box-shadow .2s, transform .15s, border-color .15s;
  &:hover {
    border-color: #ed7808;
    box-shadow: 0 8px 28px 0 rgba(237, 120, 8, .18);
    transform: translateY(-1px);
  }
}
.product-name {
  font-size: .875rem;
  font-weight: 700;
  color: #1e1e1e;
  line-height: 1.35;
}
.product-ref {
  padding-bottom: .625rem;
  font-size: .75rem;
  font-weight: 500;
  color: #82868b;
}
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: .25rem;
  padding: .2rem .55rem;
  border-radius: 100px;
  font-size: .7rem;
  font-weight: 600;
  white-space: nowrap;
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
}
.status-badge-green {
  background: #dcfce7;
  color: #15803d;
}
.status-badge-orange {
  background: #fef3c7;
  color: #92400e;
}
.validation-notice {
  display: flex;
  align-items: center;
  gap: .3rem;
  margin-bottom: .625rem;
  padding: .25rem .5rem;
  background: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 5px;
  font-size: .68rem;
  font-weight: 600;
  color: #92400e;
}
.add-row {
  display: flex;
  align-items: center;
  gap: .5rem;
  .add-btn {
    flex: 1;
    height: 30px;
    padding: 0 14px;
    font-size: .875rem;
    line-height: 1;
    letter-spacing: .4px;
    white-space: nowrap;
    border-radius: 5px !important;
  }
  .add-btn-pending {
    border-color: #fde68a !important;
    background: transparent !important;
    color: #92400e !important;
    &:hover {
      background: #fffbeb !important;
    }
  }
}
.qty-stepper {
  display: flex;
  align-items: center;
}
.qty-stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 30px;
  border: 1px solid #b8c2cc;
  background: #f6f6f6;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1;
  cursor: pointer;
  &:first-child {
    border-radius: 6px 0 0 6px;
    border-right: none;
  }
  &:last-child {
    border-radius: 0 6px 6px 0;
    border-left: none;
  }
  &:hover {
    background: #e0e0e0;
  }
}
.qty-input {
  width: 44px;
  height: 30px;
  padding: 0;
  border: 1px solid #b8c2cc;
  font-size: .875rem;
  font-weight: 700;
  text-align: center;
  outline: none;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
}
.saber-mas {
  display: block;
  margin-top: .75rem;
  padding-top: .75rem;
  border-top: 1px solid #f0f0f0;
  text-align: center;
  font-size: .82rem;
  font-weight: 600;
  color: #ed7808;
  &:hover {
    letter-spacing: .5px;
    transition: letter-spacing .15s;
  }
}
.modal-btn {
  height: auto;
  border-radius: 5px !important;
  padding: 10px 22px !important;
  font-size: .875rem;
  line-height: 1;
  letter-spacing: .4px;
  white-space: nowrap;
}
.spec-row {
  display: flex;
  justify-content: space-between;
  padding: .5rem 0;
  border-bottom: 1px solid #f0f0f0;
  font-size: .8125rem;
  &:last-of-type {
    border-bottom: none;
  }
}
</style>
